/**
 * 마감 한 줄 — 「오늘 마감 D-0」 · 「2일 지남」 · 「9/30 D-7」 (2026-09-16 · 시안 `axi-dashboard-v2`).
 *
 * ★★★ **오늘을 세는 곳은 여기 하나다.** 화면마다 따로 세면 자정 언저리에서 서로 다른 말을 한다
 *     (오늘 우리가 고친 것이 대부분 「두 곳이 따로 셌다」였다).
 * ★★★ **날짜는 «달력 날»로 센다 — 시각 차가 아니다.** `(a - b) / 86400000` 로 세면
 *     23:59 과 00:01 이 «0일»이 되어 「오늘 마감」이 하루 늦게까지 남는다.
 *     그래서 둘 다 **그 지역의 자정**으로 내린 뒤 뺀다.
 * ★ 못 읽는 값이면 **아무 말도 하지 않는다**(`null`) — 원문을 그대로 뱉으면 화면에 `2026-09-30T00:00:00Z`
 *   같은 것이 그대로 찍힌다.
 */
export type DueTone = 'today' | 'over' | 'soon' | 'later';

export interface DueLabel {
  text: string;
  tone: DueTone;
  /** 며칠 남았나 — 지났으면 음수, 오늘이면 0. */
  days: number;
}

/** 'YYYY-MM-DD' 도 ISO 시각도 받는다 — 어느 쪽이든 «그 지역의 달력 날»로 본다. */
function toLocalMidnight(value: string): Date | null {
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (ymd) return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function dueLabel(value: string | null | undefined, now: Date = new Date()): DueLabel | null {
  if (!value) return null;
  const due = toLocalMidnight(value);
  if (!due) return null;

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000);

  if (days === 0) return { text: '오늘 마감 D-0', tone: 'today', days };
  if (days < 0) return { text: `${-days}일 지남`, tone: 'over', days };
  return {
    text: `${due.getMonth() + 1}/${due.getDate()} D-${days}`,
    tone: days <= 3 ? 'soon' : 'later',
    days,
  };
}
